/**
 * Il contesto di lavoro (organizzazione e disciplina) che accompagna ogni
 * chiamata `/api/`, e il rifiuto del backend quando quel contesto non vale.
 *
 * ⛔ Perche' sta FUORI dallo stato di React (2026-10-09). Prima la patch di
 * `window.fetch` si installava in un `useEffect` del provider e leggeva il
 * contesto dallo stato, che parte da `null` e si riempie da localStorage in un
 * altro effetto. Ma React esegue gli effetti dei FIGLI prima di quelli del
 * genitore: le prime letture di ogni pagina partivano senza contesto e
 * rispondevano con i dati di tutte le organizzazioni (o con quelli giusti solo
 * alla seconda lettura). Qui il contesto si legge in modo SINCRONO da
 * localStorage (o dal link condiviso) quando la patch si installa, cioe' nel
 * primo render del provider, prima di qualunque effetto delle pagine.
 *
 * Nessuna divergenza server/client: questi valori non si disegnano. Lo stato di
 * React che disegna i selettori resta `null` al primo render e si allinea dopo
 * il montaggio, come prima.
 *
 * Il rifiuto: il backend marca con `X-Context-Rejected: organization|discipline`
 * i 403/404 dovuti al contesto (`app/dependencies.py::get_scope`). La patch lo
 * segnala a chi ascolta — il provider, che mostra il motivo del backend e offre
 * di azzerare quella parte — e restituisce la risposta intatta al chiamante.
 * Un contesto salvato e non piu' valido non si scarta in silenzio.
 */
import type { ApiErrorResponse } from '../types/errors';
import { parseApiErrorResponse } from '../utils/errorUtils';

export const ORGANIZATION_STORAGE_KEY = 'sportstack_global_orgId';
export const DISCIPLINE_STORAGE_KEY = 'sportstack_global_discCode';
export const SQUAD_TYPE_STORAGE_KEY = 'sportstack_global_squadCode';

/** L'intestazione con cui il backend dice quale parte del contesto ha rifiutato. */
export const CONTEXT_REJECTED_HEADER = 'X-Context-Rejected';

export type ContextPart = 'organization' | 'discipline';

export interface ContextRejection {
  part: ContextPart;
  /** Il motivo: quello del backend quando viene da una risposta. */
  error: ApiErrorResponse | string;
}

export interface RequestScope {
  orgId: string | null;
  disciplineCode: string | null;
}

const WRAPPED = '__sportstackScopedFetch';

let scope: RequestScope | null = null;
let pendingRejection: ContextRejection | null = null;
const listeners = new Set<(rejection: ContextRejection) => void>();

export function readStorage(key: string): string | null {
  if (typeof window === 'undefined') return null;
  try {
    return window.localStorage.getItem(key);
  } catch {
    return null;
  }
}

export function writeStorage(key: string, value: string | null): void {
  if (typeof window === 'undefined') return;
  try {
    if (value === null) window.localStorage.removeItem(key);
    else window.localStorage.setItem(key, value);
  } catch {
    // localStorage pieno o bloccato: il contesto vale per questa pagina.
  }
}

/**
 * Il contesto di partenza, letto una volta sola e in modo sincrono.
 *
 * Un collegamento condiviso (`?org=...&disciplina=...`) ha la precedenza su
 * quanto era memorizzato e lo sostituisce: chi apre il link di un torneo di
 * un'altra associazione deve ritrovarsi nel contesto giusto. Non e' un
 * permesso: il backend verifica comunque i diritti.
 */
export function currentRequestScope(): RequestScope {
  if (scope) return scope;
  if (typeof window === 'undefined') return { orgId: null, disciplineCode: null };

  let linkOrg: string | null = null;
  let linkDiscipline: string | null = null;
  try {
    const params = new URLSearchParams(window.location.search);
    linkOrg = params.get('org');
    linkDiscipline = params.get('disciplina');
  } catch {
    // Nessun accesso alla URL: si prosegue con quanto memorizzato.
  }
  if (linkOrg) writeStorage(ORGANIZATION_STORAGE_KEY, linkOrg);
  if (linkDiscipline) writeStorage(DISCIPLINE_STORAGE_KEY, linkDiscipline);

  scope = {
    orgId: linkOrg ?? readStorage(ORGANIZATION_STORAGE_KEY),
    disciplineCode: linkDiscipline ?? readStorage(DISCIPLINE_STORAGE_KEY),
  };
  return scope;
}

/** Il selettore ha cambiato il contesto: le chiamate successive lo portano. */
export function setRequestScope(next: Partial<RequestScope>): void {
  scope = { ...currentRequestScope(), ...next };
  if (pendingRejection && (
    (pendingRejection.part === 'organization' && 'orgId' in next)
    || (pendingRejection.part === 'discipline' && 'disciplineCode' in next)
  )) {
    pendingRejection = null;
  }
}

export function reportContextRejection(rejection: ContextRejection): void {
  // Il primo motivo resta: dieci chiamate rifiutate per la stessa ragione
  // non sono dieci avvisi.
  if (pendingRejection) return;
  pendingRejection = rejection;
  listeners.forEach((listener) => listener(rejection));
}

export function clearContextRejection(): void {
  pendingRejection = null;
}

/**
 * Chi ascolta riceve SUBITO un rifiuto gia' arrivato: le pagine chiamano prima
 * che il provider abbia finito di montarsi, e quel rifiuto non va perso.
 */
export function subscribeToContextRejection(
  listener: (rejection: ContextRejection) => void,
): () => void {
  listeners.add(listener);
  if (pendingRejection) listener(pendingRejection);
  return () => {
    listeners.delete(listener);
  };
}

function requestUrl(input: RequestInfo | URL): string {
  if (typeof input === 'string') return input;
  if (input instanceof URL) return input.pathname;
  return input.url;
}

/**
 * Installa l'aggiunta del contesto su `window.fetch`. Idempotente: se `fetch`
 * e' gia' quello avvolto non fa nulla; se qualcuno l'ha sostituito, avvolge il
 * nuovo.
 */
export function installScopedFetch(): void {
  if (typeof window === 'undefined' || typeof window.fetch !== 'function') return;
  const current = window.fetch as typeof fetch & { [WRAPPED]?: boolean };
  if (current[WRAPPED]) return;
  currentRequestScope();

  const original = current;
  const wrapped = async (input: RequestInfo | URL, init?: RequestInit): Promise<Response> => {
    const url = requestUrl(input);
    // Solo le chiamate alla nostra API, e solo relative: verso altre origini
    // il contesto non ha significato e sarebbe una fuga di informazione.
    const ours = url.startsWith('/api/') || url.startsWith('/auth/');
    if (!ours) return original(input, init);

    const sent = currentRequestScope();
    if (!sent.orgId && !sent.disciplineCode) return original(input, init);

    const fromRequest = typeof Request !== 'undefined' && input instanceof Request ? input.headers : undefined;
    const headers = new Headers(init?.headers ?? fromRequest);
    if (sent.orgId) headers.set('X-Organization-Id', sent.orgId);
    if (sent.disciplineCode) headers.set('X-Discipline-Code', sent.disciplineCode);

    const response = await original(input, { ...init, headers });

    const part = response?.headers?.get?.(CONTEXT_REJECTED_HEADER);
    // Un rifiuto di un contesto gia' cambiato nel frattempo non e' piu' vero.
    const still = currentRequestScope();
    const unchanged = still.orgId === sent.orgId && still.disciplineCode === sent.disciplineCode;
    if ((part === 'organization' || part === 'discipline') && unchanged) {
      let error: ApiErrorResponse | string = `HTTP ${response.status}`;
      try {
        // Letto da una COPIA: chi ha chiamato riceve la risposta intatta.
        error = await parseApiErrorResponse(response.clone());
      } catch {
        // Il corpo non si legge: resta lo stato HTTP.
      }
      reportContextRejection({ part, error });
    }
    return response;
  };
  (wrapped as typeof wrapped & { [WRAPPED]?: boolean })[WRAPPED] = true;
  window.fetch = wrapped as typeof fetch;
}

/** Solo per i test: dimentica contesto e rifiuti letti finora. */
export function resetRequestScopeForTests(): void {
  scope = null;
  pendingRejection = null;
  listeners.clear();
}
